import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import Heading from './heading';
import Text from './text';
import { PhCaretRight } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type { SemanticIntent } from 'tio-ui/color-tokens';

/**
 * Layout variant for the navigation card
 * - `vertical` — Icon centered on top, title below (quick action grid style)
 * - `horizontal` — Icon left, title/subtitle center, caret right (action row style)
 * - `gradient` — Vertical with gradient background (tool card style)
 */
export type NavCardLayout = 'vertical' | 'horizontal' | 'gradient';

/** Semantic intent for icon tint and hover treatment. */
export type NavCardIntent = SemanticIntent;

interface NavCardSignature {
  Args: {
    /** Route to navigate to */
    route: string;
    /** Optional query params to pass to LinkTo */
    query?: Record<string, string | number | boolean | null | undefined>;
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Optional subtitle text */
    subtitle?: string;
    /** Layout variant (default: 'vertical') */
    layout?: NavCardLayout;
    /** Semantic intent for the icon tint + hover treatment (default: 'brand') */
    intent?: NavCardIntent;
  };
  Element: HTMLAnchorElement;
}

const INTENT_CLASS: Record<NavCardIntent, string> = {
  brand: 'app-nav-card--brand',
  success: 'app-nav-card--success',
  info: 'app-nav-card--info',
  warning: 'app-nav-card--warning',
  danger: 'app-nav-card--danger',
  neutral: 'app-nav-card--neutral',
};

/** Navigation card with vertical, horizontal, or gradient layout. */
export default class NavCard extends Component<NavCardSignature> {
  get layout() {
    return this.args.layout ?? 'vertical';
  }

  get intentClass() {
    return INTENT_CLASS[this.args.intent ?? 'brand'];
  }

  get isVertical() {
    return this.layout === 'vertical';
  }

  get isHorizontal() {
    return this.layout === 'horizontal';
  }

  get isGradient() {
    return this.layout === 'gradient';
  }

  get queryParams() {
    return this.args.query ?? {};
  }

  <template>
    {{#if this.isHorizontal}}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="horizontal"
        @route={{@route}}
        @query={{this.queryParams}}
        class="app-nav-card app-nav-card--surface {{this.intentClass}} tio-row-loose p-5 group"
        ...attributes
      >
        <div data-test-nav-card-icon class="app-nav-card-icon p-3 rounded-xl">
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <div class="flex-1">
          <div data-test-nav-card-title class="app-nav-card-title font-semibold">
            {{@title}}
          </div>
          {{#if @subtitle}}
            <Text
              data-test-nav-card-subtitle
              @variant="inherit"
              class="app-nav-card-subtitle text-sm"
            >{{@subtitle}}</Text>
          {{/if}}
        </div>
        <PhCaretRight data-test-nav-card-caret @size={{20}} class="app-nav-card-caret" />
      </LinkTo>
    {{else if this.isGradient}}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="gradient"
        @route={{@route}}
        @query={{this.queryParams}}
        class="app-nav-card app-nav-card--gradient
          {{this.intentClass}}
          tio-stack-center gap-2 p-4 text-center"
        ...attributes
      >
        <div data-test-nav-card-icon class="app-nav-card-icon p-3 rounded-xl">
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <span data-test-nav-card-title class="app-nav-card-title tio-text-sm-medium">
          {{@title}}
        </span>
        {{#if @subtitle}}
          <span
            data-test-nav-card-subtitle
            class="app-nav-card-subtitle text-xs"
          >{{@subtitle}}</span>
        {{/if}}
      </LinkTo>
    {{else}}
      <LinkTo
        data-test-nav-card
        data-test-nav-card-layout="vertical"
        @route={{@route}}
        @query={{this.queryParams}}
        class="app-nav-card app-nav-card--surface {{this.intentClass}} p-4 group text-center"
        ...attributes
      >
        <div
          data-test-nav-card-icon
          class="app-nav-card-icon app-nav-card-icon--fill p-3 rounded-lg mx-auto w-fit mb-2"
        >
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <Heading
          data-test-nav-card-title
          @level={{3}}
          class="app-nav-card-title tio-text-sm-semibold"
        >{{@title}}</Heading>
        {{#if @subtitle}}
          <Text
            data-test-nav-card-subtitle
            @variant="inherit"
            class="app-nav-card-subtitle text-xs mt-1 hidden sm:block"
          >{{@subtitle}}</Text>
        {{/if}}
      </LinkTo>
    {{/if}}
  </template>
}

export type { NavCardSignature };
